// Custom hook for managing real mining data from mining-dutch.nl APIs

import { useEffect, useState, useCallback } from 'react';
import { useAppContext } from '../context/AppContext';
import { miningServices } from '../services';

export const useMiningData = () => {
  const { state, dispatch } = useAppContext();
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Initialize mining services (simplified for real APIs)
  const initializeServices = useCallback(async () => {
    try {
      console.log('🚀 Initializing real mining services...');
      
      // Test API connectivity
      const testStats = await miningServices.getDashboardStats();
      console.log('✅ API connectivity test successful:', testStats);
      
      setIsInitialized(true);
      console.log('✅ Mining services initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize mining services:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { key: 'dashboard', value: 'Failed to connect to mining APIs' } 
      });
      // Still set as initialized to allow retry
      setIsInitialized(true);
    }
  }, [dispatch]);

  // Fetch dashboard statistics from real API
  const fetchDashboardStats = useCallback(async () => {
    console.log('📊 Fetching dashboard statistics...');
    dispatch({ type: 'SET_LOADING', payload: { key: 'dashboard', value: true } });
    
    try {
      const stats = await miningServices.getDashboardStats();
      
      dispatch({ type: 'SET_DASHBOARD_STATS', payload: stats });
      dispatch({ type: 'SET_ERROR', payload: { key: 'dashboard', value: null } });
      
      console.log('✅ Dashboard stats updated:', stats);
    } catch (error) {
      console.error('❌ Failed to fetch dashboard stats:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { key: 'dashboard', value: 'Failed to load dashboard data' } 
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'dashboard', value: false } });
    }
  }, [dispatch]);

  // Fetch miners data from real API
  const fetchMinersData = useCallback(async () => {
    console.log('⛏️ Fetching miners data...');
    dispatch({ type: 'SET_LOADING', payload: { key: 'miners', value: true } });
    
    try {
      const miners = await miningServices.getAllWorkers();
      
      dispatch({ type: 'SET_MINERS', payload: miners });
      dispatch({ type: 'SET_ERROR', payload: { key: 'miners', value: null } });
      
      console.log(`✅ Miners data updated: ${miners.length} miners`);
    } catch (error) {
      console.error('❌ Failed to fetch miners data:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: { key: 'miners', value: 'Failed to load miners data' } 
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'miners', value: false } });
    }
  }, [dispatch]);

  // Fetch crypto progress data
  const fetchCryptoProgress = useCallback(async () => {
    console.log('💎 Fetching crypto progress...');
    
    try {
      const cryptoData = await miningServices.getCryptoProgress();
      console.log('✅ Crypto progress updated:', cryptoData);
      
      // Store in a way that components can access it
      // We'll add this to context if needed
      return cryptoData;
    } catch (error) {
      console.error('❌ Failed to fetch crypto progress:', error);
      return { doge: null, bitcoin: null };
    }
  }, []);

  // Fetch average hashrate for chart
  const fetchAverageHashrate = useCallback(async () => {
    console.log('📈 Fetching average hashrate...');
    
    try {
      const hashrateData = await miningServices.getAverageHashrate();
      console.log('✅ Average hashrate updated:', hashrateData);
      
      return hashrateData;
    } catch (error) {
      console.error('❌ Failed to fetch average hashrate:', error);
      return {
        current: 0,
        average: 0,
        efficiency: 0,
        temperature: 0
      };
    }
  }, []);

  // Refresh all data
  const refreshAllData = useCallback(async () => {
    console.log('🔄 Refreshing all mining data...');
    setLastFetchTime(Date.now());
    
    await Promise.all([
      fetchDashboardStats(),
      fetchMinersData(),
      fetchCryptoProgress(),
      fetchAverageHashrate()
    ]);
    
    console.log('✅ All data refreshed successfully');
  }, [fetchDashboardStats, fetchMinersData, fetchCryptoProgress, fetchAverageHashrate]);

  // Initialize services on mount
  useEffect(() => {
    if (!isInitialized) {
      initializeServices();
    }
  }, [isInitialized, initializeServices]);

  // Fetch initial data after services are initialized
  useEffect(() => {
    if (isInitialized) {
      refreshAllData();
    }
  }, [isInitialized, refreshAllData]);

  // Set up auto-refresh intervals (matching Android app behavior)
  useEffect(() => {
    if (!isInitialized || !state.settings.autoRefresh) return;

    console.log('⏰ Setting up auto-refresh intervals...');
    
    const intervals = [
      setInterval(fetchDashboardStats, 60000), // 1 minute
      setInterval(fetchMinersData, 30000), // 30 seconds
      setInterval(fetchCryptoProgress, 120000), // 2 minutes
    ];

    return () => {
      console.log('🛑 Clearing auto-refresh intervals');
      intervals.forEach(clearInterval);
    };
  }, [isInitialized, state.settings.autoRefresh, fetchDashboardStats, fetchMinersData, fetchCryptoProgress]);

  // Calculate derived statistics
  const derivedStats = {
    totalHashrateFormatted: state.dashboardStats 
      ? `${(state.dashboardStats.totalHashrate / 1000000000).toFixed(2)} GH/s`
      : '---',
    
    averageHashratePerWorker: state.dashboardStats && state.dashboardStats.totalWorkers > 0
      ? state.dashboardStats.totalHashrate / state.dashboardStats.totalWorkers
      : 0,
    
    onlineMinersCount: state.miners.filter(m => m.status === 'online').length,
    
    offlineMinersCount: state.miners.filter(m => m.status === 'offline').length,
    
    errorMinersCount: state.miners.filter(m => m.status === 'error').length,
    
    // Calculate total hashrate by algorithm
    sha256Hashrate: state.miners
      .filter(m => m.algorithm === 'SHA256' && m.status === 'online')
      .reduce((sum, miner) => sum + miner.hashrate, 0),
    
    scryptHashrate: state.miners
      .filter(m => m.algorithm === 'Scrypt' && m.status === 'online')
      .reduce((sum, miner) => sum + miner.hashrate, 0),
    
    lastUpdateTime: lastFetchTime
  };

  return {
    // State
    ...state,
    isInitialized,
    derivedStats,
    
    // Actions
    refreshAllData,
    fetchDashboardStats,
    fetchMinersData,
    fetchCryptoProgress,
    fetchAverageHashrate,
    
    // Service status
    apiStatus: isInitialized ? 'connected' : 'connecting',
    lastFetchTime
  };
};

export default useMiningData;